/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { useHistory } from 'react-router-dom';

import { Segment, Sidebar } from '@mairistem/composants';

import { ASSISTANT_STEPS, STEPS_BY_TYPE_ACTE } from '@/constants';
import { baseUrlApp } from '@/routes/base';
import { Steps } from '@/types';
import { AssistantContext } from '@/contexts';
import { useCreateActe } from '@/hooks';
import { useDocumentStore } from '@/zustand';

import ASSISTANT_CONFIG from './assistantConfig';
import {
  getStepPosition,
  getStepNavigation,
  getValidationRange,
} from './stepNavigation';
import AssistantApercuDocument from './AssistantApercuDocument';
import AssistantFooter from './AssistantFooter';
import AssistantHeader from './AssistantHeader';
import AssistantModalFin from './AssistantModalFIn';
import AssistantModalErrorStep from './AssistantModalErrorStep';
import AssistantModalErrorStepFin from './AssistantModalErrorStepFIn';

const Assistant = () => {
  // #region Declarations

  const {
    selectedTypeActe,
    data,
    selectedRegistre,
    mentions,
    errors,
    setErrors,
  } = React.useContext(AssistantContext);

  const history = useHistory();

  /** Index de l'étape actuelle */
  const [currentStepIndex, setCurrentStepIndex] = React.useState<Steps>(
    Steps.Acte,
  );
  /** En chargement (soumission en cours) */
  const [loading, setLoading] = React.useState<boolean>(false);
  /** Affichage de la modale de fin de saisie (identifiant de l'acte créé) */
  const [openModalFin, setOpenModalFin] = React.useState<string | null>(null);
  /** Confirmation d'erreur sur la step, on stocke l'identifiant de la next step si validation */
  const [showModalErrorStep, setShowModalErrorStep] =
    React.useState<Steps | null>(null);
  /** Modal d'erreur de saisie globale avant d'envoyer au back */
  const [showModalErrorStepFin, setShowModalErrorStepFin] =
    React.useState<boolean>(false);

  // Hook générique de création d'acte numérisé (naissance ET reconnaissance,
  // paramétré par `selectedTypeActe` à l'appel : cf. `assistantConfig.tsx`
  // pour l'explication du choix de ne pas mettre ce hook dans le registre).
  const { createActe } = useCreateActe();

  /** Document scanné sélectionné dans la modale de saisie (aperçu + envoi à la création). */
  const document = useDocumentStore((state) => state.document);

  /** Configuration (composants/validators/nom de l'acte) du type d'acte sélectionné. */
  const config = selectedTypeActe
    ? ASSISTANT_CONFIG[selectedTypeActe]
    : undefined;

  /** Étapes de l'assistant pour le type d'acte sélectionné */
  const steps = React.useMemo(
    () =>
      selectedTypeActe
        ? _.pick(ASSISTANT_STEPS, STEPS_BY_TYPE_ACTE[selectedTypeActe] ?? [])
        : {},
    [selectedTypeActe],
  );

  /**
   * Étapes du type d'acte sélectionné, dans l'ORDRE réel du parcours (ex.
   * reconnaissance : `[Acte, Enfant, Parents, Mentions]`). Différent types
   * d'acte peuvent avoir des étapes différentes (ex. naissance n'a pas
   * `Enfant` comme étape séparée) : toute la navigation (précédent/suivant,
   * validation en chaîne, numérotation du header) doit se baser sur la
   * POSITION dans ce tableau, jamais sur la valeur numérique brute de
   * `Steps`, qui n'est qu'un identifiant stable.
   */
  const orderedSteps = React.useMemo(
    () =>
      selectedTypeActe ? (STEPS_BY_TYPE_ACTE[selectedTypeActe] ?? []) : [],
    [selectedTypeActe],
  );

  // #endregion

  // #region Functions

  const handleErrors = React.useCallback(
    (fromStep: Steps, toStep?: Steps): boolean => {
      let hasErrors = false;
      const newErrors: Record<number, Record<string, any> | null> = {};

      getValidationRange(orderedSteps, fromStep, toStep).forEach((step) => {
        const validator = config?.validators[step];
        const stepErrors = validator
          ? validator({ data, selectedRegistre, mentions })
          : null;

        if (!_.isEmpty(stepErrors)) {
          hasErrors = true;
        }
        newErrors[step] = stepErrors;
      });

      setErrors((prev: Record<string, any>) => ({
        ...prev,
        ...newErrors,
      }));

      return hasErrors;
    },
    [config, data, selectedRegistre, mentions, setErrors, orderedSteps],
  );

  /**
   * Callback appelé lors du changement de step
   */
  const handleStepChange = React.useCallback(
    (stepIndex: Steps) => {
      // Si on recule, on change directement d'étape sans validation
      if (
        getStepPosition(orderedSteps, stepIndex) <=
        getStepPosition(orderedSteps, currentStepIndex)
      ) {
        setCurrentStepIndex(stepIndex);
        return;
      }

      const hasErrors = handleErrors(currentStepIndex, stepIndex);

      // Si on a des erreurs on affiche la modal de confirmation
      if (hasErrors) {
        setShowModalErrorStep(stepIndex);
      } else {
        setCurrentStepIndex(stepIndex);
      }
    },
    [currentStepIndex, handleErrors, orderedSteps],
  );

  const handleValidateModalErrorStep = React.useCallback(() => {
    setCurrentStepIndex(showModalErrorStep as Steps);
    setShowModalErrorStep(null);
  }, [showModalErrorStep]);

  const handleConsulterActe = React.useCallback(() => {
    window.location.href = `${baseUrlApp}${selectedTypeActe}/${openModalFin}`;
  }, [selectedTypeActe, openModalFin]);

  const handleRetourDashboard = React.useCallback(
    (openSaisieEvenement: boolean) => {
      history.push(
        openSaisieEvenement
          ? `${baseUrlApp}?openSaisieEvenement=1`
          : baseUrlApp,
      );
    },
    [history],
  );

  const handleNouvelActe = React.useCallback(() => {
    handleRetourDashboard(true);
  }, [handleRetourDashboard]);

  const handleRetourAccueil = React.useCallback(() => {
    handleRetourDashboard(false);
  }, [handleRetourDashboard]);

  const handleValidateModalErrorStepFin = React.useCallback(() => {
    // On trouve le premier index qui a des erreurs non vides
    const stepToGo = Object.keys(errors).find((key) => !_.isEmpty(errors[key]));
    const stepToDisplay = parseInt(stepToGo as string, 10) as Steps;
    setCurrentStepIndex(stepToDisplay);
    setShowModalErrorStepFin(false);
  }, [errors]);

  const handleValidate = React.useCallback(() => {
    setLoading(true);

    createActe(selectedTypeActe, data, mentions, selectedRegistre, document)
      .then((response: any) => {
        setOpenModalFin(response.identifiant);
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'acte",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    createActe,
    selectedTypeActe,
    data,
    mentions,
    selectedRegistre,
    document,
  ]);

  /**
   * Callback appelé lors du clic sur le bouton "Terminer"
   */
  const handleFinish = React.useCallback(() => {
    // Garde-fou : type d'acte sans configuration connue (ex. `?type=mariage`,
    // pas encore implémenté) → on n'essaie pas de poster sur une URL
    // inexistante, on laisse `stepToDisplay` afficher le message d'erreur.
    if (!config) {
      return;
    }

    // Sans `toStep`, `handleErrors` valide toutes les étapes du parcours.
    const hasErrors = handleErrors(orderedSteps[0]);

    if (!hasErrors) {
      handleValidate();
    } else {
      setShowModalErrorStepFin(true);
    }
  }, [config, handleErrors, handleValidate, orderedSteps]);

  // #endregion

  // #region Components

  const stepToDisplay = React.useMemo(() => {
    const StepComponent = config?.components[currentStepIndex];

    return StepComponent ? (
      <StepComponent />
    ) : (
      <h1>Une erreur est survenue...</h1>
    );
  }, [config, currentStepIndex]);

  /** Navigation précédent/suivant du footer, dérivée de la position dans `orderedSteps`. */
  const { isFirstStep, isLastStep, previousStep, nextStep } = getStepNavigation(
    orderedSteps,
    currentStepIndex,
  );

  return (
    <Sidebar.Pushable
      style={{ height: '100%', overflow: 'hidden', position: 'relative' }}
    >
      <Sidebar.Pusher>
        <div
          style={{
            height: '100%',
            display: 'flex',
            gap: '1em',
            padding: '1em',
          }}
        >
          <Segment
            raised
            style={{
              flex: 1,
              minWidth: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              margin: 0,
              backgroundColor: 'white',
              borderRadius: '16px',
            }}
          >
            <AssistantHeader
              currentStepIndex={currentStepIndex}
              onStepChange={handleStepChange}
              steps={steps}
              orderedSteps={orderedSteps}
            />
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                overflowY: 'auto',
                padding: '1em 1em',
              }}
            >
              {stepToDisplay}
            </div>
            <AssistantFooter
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
              onPrevious={() => handleStepChange(previousStep)}
              onNext={() => handleStepChange(nextStep)}
              onFinish={handleFinish}
              loading={loading}
            />
          </Segment>
          <Segment
            raised
            data-testid="assistant-apercu-acte"
            style={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              margin: 0,
              padding: 0,
              overflow: 'hidden',
              backgroundColor: 'white',
              borderRadius: '16px',
            }}
          >
            <AssistantApercuDocument />
          </Segment>
        </div>
        <AssistantModalFin
          open={!_.isNil(openModalFin)}
          typeActe={selectedTypeActe ?? ''}
          nomActe={config ? config.getNomActe(data) : ''}
          onClose={() => setOpenModalFin(null)}
          onConsulterActe={handleConsulterActe}
          onNouvelActe={handleNouvelActe}
          onRetourAccueil={handleRetourAccueil}
        />
        {showModalErrorStep !== null && (
          <AssistantModalErrorStep
            onValidate={handleValidateModalErrorStep}
            onClose={() => setShowModalErrorStep(null)}
          />
        )}
        {showModalErrorStepFin && (
          <AssistantModalErrorStepFin
            onValidate={handleValidateModalErrorStepFin}
            onClose={() => setShowModalErrorStepFin(false)}
          />
        )}
      </Sidebar.Pusher>
    </Sidebar.Pushable>
  );
};

export default Assistant;
